import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import moment from 'moment';
import { RenderForAI } from '@components/common';
import { PAGE_TYPE } from '@utils/constants';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { tid } from '@utils/test';
import SampledDataWarning from '@components/common/SampledDataWarning';
import Latch from '@components/common/Latch';
import { MODE_ALERT_DIAGNOSTICS } from '@store/trends/constants';
import MemoDatePicker from './DatePickers';
import DashboardTabs from '../../common/Dashboard/DashboardTabs';
import styles from './index.less';
import { calcComparisonInterval } from './utils/dates';
import DataSourceFilter from '../DataSourceFilter';
import AutoRefreshToggle from './AutoRefreshToggle';
import useSetExportBase from './hooks/useSetExportBase';
import FilterDialogSwitch from './FilterDialogSwitch';
import {
  dataAccessDurationSelector,
  intervalSelector,
  realTimeModeOnTrendsSelector,
} from '@src/store/common/selectors';
import { setComparisonInterval } from '@store/summaryContainer/comparison/actions';
import { isSharingAtom, hasExplicitComparisonAtom } from '@src/atoms/pageMeta';
import { useStartObservingHeight } from '@src/atoms/page-styles';
import { selector as summaryContainerSelector } from '@store/summaryContainer';
import { applyFilter, updateInterval } from '@src/store/common/actions';
import { drillUp } from '@src/store/summaryContainer/drilldown/actions';
import { selector as summaryComparisonSelector } from '@store/summaryContainer/comparison';
import { modeSelector } from '@src/store/trends/selectors';
import WhatsNew from './WhatsNew';
import { SUMMARY_PANEL_CONTROLS_ID } from './utils';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { DeletedMetricMessage } from '@components/common/DeletedMetric';
import useHideTabs from './hooks/useHideTabs';

function SummaryContainer({ id, hasBreadcrumb = false }) {
  const errorData = useAtomValue(errorDataAtom);
  const dispatch = useDispatch();
  const summaryDatePickerRef = useRef();
  const comparisonDatePickerRef = useRef();
  const filterPanelRef = useRef(null);

  // workaround to hide tabs in Nexa Mode
  const hideTabs = useHideTabs();

  const interval = useSelector(intervalSelector);

  const isSharing = useAtomValue(isSharingAtom);
  const hasExplicitComparison = useAtomValue(hasExplicitComparisonAtom);
  const durationAllowed = useSelector(dataAccessDurationSelector);

  const { drilldown } = useSelector(summaryContainerSelector);
  const comparison = useSelector(summaryComparisonSelector);
  const { interval: comparisonInterval } = comparison;
  const { mode } = useSelector(modeSelector);
  const liveModeSelected = useSelector(realTimeModeOnTrendsSelector);

  // shouldn't datepicker do this?
  // reset flow is doing this for regular trends but for presets this is required
  // TODO: cleanup and move to as an init action
  const updateComparisonInterval = () => {
    const { from, to } = calcComparisonInterval(interval, durationAllowed);

    // Align seconds of compare interval with current interval
    // Needed when compare series is shown in trends diagnostics chart
    const fromSeconds = moment(interval.from).seconds();
    const toSeconds = moment(interval.to).seconds();
    if (from) {
      from.seconds(fromSeconds);
    }
    if (to) {
      to.seconds(toSeconds);
    }

    // Don't set comparison interval automatically when it's a sharing page or has explicit comparison
    // (e.g., Nexa DoD merged links provide explicit comparison intervals)
    if (from && to && !isSharing && !hasExplicitComparison) {
      dispatch(setComparisonInterval(from.format(), to.format()));
    }
  };

  useEffect(() => {
    updateComparisonInterval();
  }, []);

  // Observe height of filter panel, will update the top distance of each fixed element
  useStartObservingHeight(filterPanelRef);

  const applyFilterFromBreadcrumb = (key) => {
    const newFilter: any = [];
    drilldown.slice(1, key + 1).forEach((item) => {
      if (Array.isArray(item.rule[0])) {
        item.rule.forEach((r) => {
          newFilter.push(r);
        });
      } else {
        newFilter.push(item.rule);
      }
    });
    const allRules = getRulesFromFilter(newFilter, true);
    const filterToApply: any = getFilterFromRules(allRules, true);

    // identifier for preset filters
    const keyFilterObj = drilldown[key];
    if (keyFilterObj.presetFid) {
      filterToApply.presetFid = keyFilterObj.presetFid;
    }

    dispatch(applyFilter(filterToApply));
    dispatch(drillUp(key));
  };

  const hasIntervalChanged = (newInterval) => {
    try {
      if (
        newInterval[0].isSame(moment(interval.from)) &&
        newInterval[1].isSame(moment(interval.to))
      ) {
        return false;
      }
    } catch (err) {
      // pass
      // falls back to current behaviour
    }
    return true;
  };

  // HIDE OR SHOW WHATS NEW Based on Requirement
  const showWhatsNew = false;

  // check if summary interval changed before triggering redux action
  // as there are multiple listeners
  const validateUpdateInterval = (dates, short) => {
    if (hasIntervalChanged(dates)) {
      dispatch(updateInterval(dates[0].format(), dates[1].format(), short, 'trends'));
    }
  };

  let pageType = -1;
  if (isSharing) {
    pageType = PAGE_TYPE.SHARING;
  }

  return (
    <>
      <div className="header-layout">
        <div className="left">
          {hideTabs ? null : <DashboardTabs pageType={pageType} id={id} />}
        </div>
        {showWhatsNew ? <WhatsNew /> : null}
      </div>
      <div
        id="filter-panel-container"
        ref={filterPanelRef}
        className={styles.summaryContainer}
        style={{ top: hasBreadcrumb ? '43px' : '0px' }}
        {...tid('summary-panel')}
      >
        <div className={styles.topWrapper} {...tid('controls')} id={SUMMARY_PANEL_CONTROLS_ID}>
          <div className={styles.filterContainer}>
            <FilterDialogSwitch
              drilldown={drilldown}
              applyFilterFromBreadcrumb={applyFilterFromBreadcrumb}
            />
          </div>
          {errorData.fields.length ? <DeletedMetricMessage /> : null}
          <div className={styles.controls}>
            {mode !== MODE_ALERT_DIAGNOSTICS && (
              <RenderForAI>
                <DataSourceFilter />
              </RenderForAI>
            )}
            <SampledDataWarning />
            {liveModeSelected ? <AutoRefreshToggle /> : null}
            <div>
              <MemoDatePicker
                key={`${JSON.stringify(interval)}/${JSON.stringify(comparisonInterval)}`}
                interval={interval}
                comparisonInterval={comparisonInterval}
                validateUpdateInterval={validateUpdateInterval}
                summaryDatePickerRef={summaryDatePickerRef}
                comparisonDatePickerRef={comparisonDatePickerRef}
                durationAllowed={durationAllowed}
              />
            </div>
          </div>
        </div>
        <Latch useHook={useSetExportBase} params={{}} />
      </div>
    </>
  );
}

export default SummaryContainer;
